import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaEnvelope, 
  FaUser, 
  FaComment, 
  FaPhone, 
  FaBuilding,
  FaTag,
  FaPaperPlane,
  FaCheckCircle,
  FaHeadset,
  FaDownload
} from "react-icons/fa";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Logo from "../components/Logo";
import LoadingIndicator from "../components/LoadingIndicator";
import api from "../api";
import "../styles/ContactUs.css";

function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    category: "general"
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const categories = [
    { value: "general", label: "General Inquiry" },
    { value: "support", label: "Technical Support" },
    { value: "sales", label: "Sales & Pricing" },
    { value: "partnership", label: "Partnership" },
    { value: "security", label: "Security Report" },
    { value: "feedback", label: "Feedback" },
    { value: "other", label: "Other" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return false;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return false;
    }
    if (!formData.subject.trim()) {
      setError("Subject is required");
      return false;
    }
    if (!formData.message.trim()) {
      setError("Message is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    // Execute reCAPTCHA v3
    if (!executeRecaptcha) {
      setError("reCAPTCHA not ready. Please try again.");
      setLoading(false);
      return;
    }

    const recaptchaToken = await executeRecaptcha('submit_contact');
    if (!recaptchaToken) {
      setError("reCAPTCHA verification failed. Please try again.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/api/contact/", {
        ...formData,
        recaptcha: recaptchaToken
      });

      if (response.status === 201) {
        setSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
          category: "general"
        });

      }
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errorMessages = [];
          Object.keys(errorData).forEach(field => {
            if (Array.isArray(errorData[field])) {
              errorMessages.push(...errorData[field]);
            } else {
              errorMessages.push(errorData[field]);
            }
          });
          setError(errorMessages.join(' '));
        } else {
          setError(errorData);
        }
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="contact-container">
        <header className="contact-header">
          <Logo size="small" showText={false} linkTo="/" />
          <nav className="nav-links">
            <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
            <Link to="/login" className="login-link">Login</Link>
            <Link to="/register" className="register-link">Register</Link>
          </nav>
        </header>

        <div className="contact-content">
          <div className="success-card">
            <FaCheckCircle className="success-icon" />
            <h2>Thank You!</h2>
            <p>Your message has been successfully sent. We'll get back to you as soon as possible.</p>
            <div className="success-actions">
              <Link to="/" className="btn btn-primary">Return to Home</Link>
              <button onClick={() => setSuccess(false)} className="btn btn-outline">
                Send Another Message
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-container">
      <header className="contact-header">
        <Logo size="small" showText={false} linkTo="/" />
        <nav className="nav-links">
          <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
          <Link to="/login" className="login-link">Login</Link>
          <Link to="/register" className="register-link">Register</Link>
        </nav>
      </header>

      <div className="contact-content">
        <div className="contact-card">
          <div className="contact-header-content">
            <FaHeadset className="contact-icon" />
            <h2>Contact Us</h2>
            <p>Get in touch with our team. We're here to help you.</p>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name <span className="required">*</span></label>
                <div className="input-group">
                  <FaUser className="input-icon" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address <span className="required">*</span></label>
                <div className="input-group">
                  <FaEnvelope className="input-icon" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <div className="input-group">
                  <FaPhone className="input-icon" />
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(Optional)"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="company">Company</label>
                <div className="input-group">
                  <FaBuilding className="input-icon" />
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="(Optional)"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category <span className="required">*</span></label>
                <div className="input-group">
                  <FaTag className="input-icon" />
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject <span className="required">*</span></label>
                <div className="input-group">
                  <FaComment className="input-icon" />
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Brief description"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message <span className="required">*</span></label>
              <div className="input-group">
                <FaComment className="input-icon textarea-icon" />
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Please provide detailed information about your inquiry..."
                  rows="5"
                  required
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <FaPaperPlane />
                  Send Message
                </>
              )}
            </button>
          </form>

          <div className="form-footer">
            <Link to="/">← Back to Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;
